import { FileExcelOutlined, PrinterOutlined } from '@ant-design/icons';
import { ConfigProvider, Empty, Select, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { CustomAreaChart } from '@/shared/components/charts/AreaChart/AreaChart';
import { CustomPieChart } from '@/shared/components/charts/PieChart/PieChart';
import { api } from '@/shared/api/api';
import { StatisticsItem } from './components/StatisticsItem/StatisticsItem';
import styles from './StatisticsPage.module.css';
import { handleExportToExcel } from './utils';
import { getChangeType } from './utils/getChangeType';
import { handlePrint } from './utils/handlePrint';
import { useResize } from '@/shared/utils';

const StatisticsPage = () => {
  const { Title } = Typography;
  const { isMobile } = useResize();
  const [usersData, setUsersData] = useState([]);
  const [devicesData, setDevicesData] = useState([]);
  const [onlineData, setOnlineData] = useState([]);
  const [onlineDevicesData, setOnlineDevicesData] = useState([]);
  const [armyPosts, setArmyPosts] = useState([]);
  const [selectedArmyPost, setSelectedArmyPost] = useState(null);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      api
        .get('/api/statistics/getUserStatistics', {
          signal: abortController.signal,
        })
        .then(({ data }) => setUsersData(data));
      api
        .get(`/api/statistics/getDeviceStatistics?armyPost=${selectedArmyPost || null}`, {
          signal: abortController.signal,
        })
        .then(({ data }) => setDevicesData(data));
      api
        .get('/api/statistics/getUsersOnlineStatistics', {
          signal: abortController.signal,
        })
        .then(({ data }) => setOnlineData(data));
      api
        .get('/api/statistics/getUniqueArmyPosts', {
          signal: abortController.signal,
        })
        .then(({ data }) => setArmyPosts(data));
    } catch (error) {
      console.error(error);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    api
      .get(`/api/statistics/getDeviceStatistics?armyPost=${selectedArmyPost || null}`)
      .then(({ data }) => setDevicesData(data));
    api
      .get(`/api/statistics/getDevicesOnlineStatistics?armyPost=${selectedArmyPost || null}`)
      .then(({ data }) => setOnlineDevicesData(data));
  }, [selectedArmyPost]);

  return (
    <div className={styles.usersStatisticsPage}>
      <div className={styles.pageHeader}>
        <Title>Статистика</Title>
        <div className={styles.buttonBox}>
          <button className={styles.printButton} onClick={handlePrint}>
            Распечатать
            <PrinterOutlined className={styles.printIcon} />
          </button>
        </div>
      </div>
      <div id='printableData'>
        <div className={styles.statisticsItemsWrapper}>
          <Title level={4}>Онлайн пользователи за</Title>
          {
            <div className={styles.statisticsItems}>
              <StatisticsItem
                label='последние сутки'
                suffix='чел.'
                value={onlineData?.dailyData}
                changeType={getChangeType(
                  onlineData?.dailyData,
                  onlineData?.dailyChartData?.[onlineData?.dailyChartData?.length - 1]?.['Онлайн пользователи']
                )}
              />
              <StatisticsItem
                label='последнюю неделю'
                suffix='чел.'
                value={onlineData?.weeklyData}
                changeType={getChangeType(
                  onlineData?.weeklyData,
                  onlineData?.weeklyChartData?.[onlineData?.weeklyChartData?.length - 1]?.['Онлайн пользователи']
                )}
              />
              <StatisticsItem
                label='последний месяц'
                suffix='чел.'
                value={onlineData?.monthlyData}
                changeType={getChangeType(
                  onlineData?.monthlyData,
                  onlineData?.monthlyChartData?.[onlineData?.monthlyChartData?.length - 1]?.['Онлайн пользователи']
                )}
              />
              <StatisticsItem
                label='последний год'
                suffix='чел.'
                value={onlineData?.yearlyData}
                changeType={getChangeType(
                  onlineData?.yearlyData,
                  onlineData?.yearlyChartData?.[onlineData?.yearlyChartData?.length - 1]?.['Онлайн пользователи']
                )}
              />
            </div>
          }
        </div>
        <div className={styles.barChartsCard}>
          <div className={styles.doubleChartBox}>
            <CustomAreaChart
              data={onlineData?.dailyChartData}
              title='Онлайн пользователи по дням'
              categories={['Онлайн пользователи']}
            />
            <CustomAreaChart
              data={onlineData?.weeklyChartData}
              title='Онлайн пользователи по неделям'
              categories={['Онлайн пользователи']}
            />
          </div>
          <div className={styles.doubleChartBox}>
            <CustomAreaChart
              data={onlineData?.monthlyChartData}
              title='Онлайн пользователи по месяцам'
              categories={['Онлайн пользователи']}
            />
            <CustomAreaChart
              data={onlineData?.yearlyChartData}
              title='Онлайн пользователи по годам'
              categories={['Онлайн пользователи']}
            />
          </div>
        </div>
        <div className={styles.barChartsCard}>
          <div className={styles.doubleChartBox}>
            <CustomAreaChart
              data={usersData?.dayChartData}
              title='Новые пользователи за сутки'
              categories={['Новые пользователи']}
            />
            <CustomAreaChart
              data={usersData?.weekChartData}
              title='Новые пользователи за неделю'
              categories={['Новые пользователи']}
            />
          </div>
          <CustomAreaChart
            data={usersData?.monthChartData}
            title='Новые пользователи за месяц'
            categories={['Новые пользователи']}
          />
        </div>
        <div className={styles.pageHeader}>
          <Title>Статистика по устройствам {selectedArmyPost && `(${selectedArmyPost})`}</Title>
          <ConfigProvider renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Нет данных' />}>
            <Select
              options={armyPosts}
              placeholder='Военная часть'
              showSearch
              allowClear
              value={selectedArmyPost}
              onChange={e => setSelectedArmyPost(e)}
            />
          </ConfigProvider>
        </div>
        <div className={styles.statisticsItemsWrapper}>
          <Title level={4}>Онлайн устройства за</Title>
          {
            <div className={styles.statisticsItems}>
              <StatisticsItem
                label='последние сутки'
                suffix='уст.'
                value={onlineDevicesData?.dailyData}
                changeType={getChangeType(
                  onlineDevicesData?.dailyData,
                  onlineDevicesData?.dailyChartData?.[onlineDevicesData?.dailyChartData?.length - 1]?.[
                    'Онлайн устройства'
                  ]
                )}
              />
              <StatisticsItem
                label='последнюю неделю'
                suffix='уст.'
                value={onlineDevicesData?.weeklyData}
                changeType={getChangeType(
                  onlineDevicesData?.weeklyData,
                  onlineDevicesData?.weeklyChartData?.[onlineDevicesData?.weeklyChartData?.length - 1]?.[
                    'Онлайн устройства'
                  ]
                )}
              />
              <StatisticsItem
                label='последний месяц'
                suffix='уст.'
                value={onlineDevicesData?.monthlyData}
                changeType={getChangeType(
                  onlineDevicesData?.monthlyData,
                  onlineDevicesData?.monthlyChartData?.[onlineDevicesData?.monthlyChartData?.length - 1]?.[
                    'Онлайн устройства'
                  ]
                )}
              />
              <StatisticsItem
                label='последний год'
                suffix='уст.'
                value={onlineDevicesData?.yearlyData}
                changeType={getChangeType(
                  onlineDevicesData?.yearlyData,
                  onlineDevicesData?.yearlyChartData?.[onlineDevicesData?.yearlyChartData?.length - 1]?.[
                    'Онлайн устройства'
                  ]
                )}
              />
            </div>
          }
        </div>
        <div className={styles.barChartsCard}>
          <div className={styles.doubleChartBox}>
            <CustomAreaChart
              data={onlineDevicesData?.dailyChartData}
              title='Онлайн устройства по дням'
              categories={['Онлайн устройства']}
            />
            <CustomAreaChart
              data={onlineDevicesData?.weeklyChartData}
              title='Онлайн устройства по неделям'
              categories={['Онлайн устройства']}
            />
          </div>
          <div className={styles.doubleChartBox}>
            <CustomAreaChart
              data={onlineDevicesData?.monthlyChartData}
              title='Онлайн устройства по месяцам'
              categories={['Онлайн устройства']}
            />
            <CustomAreaChart
              data={onlineDevicesData?.yearlyChartData}
              title='Онлайн устройства по годам'
              categories={['Онлайн устройства']}
            />
          </div>
        </div>
        <div className={styles.barChartsCard}>
          <div className={styles.doubleChartBox}>
            <CustomAreaChart
              data={devicesData?.dayChartData}
              title='Новые устройства за сутки'
              categories={['Новые устройства']}
            />
            <CustomAreaChart
              data={devicesData?.weekChartData}
              title='Новые устройства за неделю'
              categories={['Новые устройства']}
            />
          </div>
          <CustomAreaChart
            data={devicesData?.monthChartData}
            title='Новые устройства за месяц'
            categories={['Новые устройства']}
          />
        </div>
        <div className={styles.pieChartCard}>
          <CustomPieChart
            handleCellClick={setSelectedArmyPost}
            title={'Статистика по военным частям'}
            data={devicesData?.armyPostsChartData}
            outerRadius={isMobile ? 100 : 150}
          />
          <button className={styles.downloadButton} onClick={() => handleExportToExcel(devicesData.armyPostsChartData)}>
            Экспорт в Excel
            <FileExcelOutlined className={styles.excelIcon} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;
